/**
 * Compliance Cone-of-Light (doc 147 §6.6 E1 + PR Q3 PII matrix + simulate).
 *
 * The previous view ran the pre-PR-Q3 `COMPLIANCE_REGIMES` (heuristic
 * scope predicates) and exported a markdown dossier. Q3 swaps the body
 * for a PII matrix per regime, keeps the dossier export, and adds a
 * Simulate-Response panel that strikes through PII fields in a sample
 * payload — legal hands it to regulators.
 *
 * Regimes here are the PR Q3 set (gdpr / ccpa / hipaa) — narrower than
 * the legacy COMPLIANCE_REGIMES list which mixed PII regimes with
 * domain-specific scopes (COPPA, TILA, etc.). Those scopes are still
 * available via the markdown export; the matrix focuses on PII.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import {
  buildPiiMatrix,
  simulateScrubbed,
  REGIME_RULES,
  type RegimeId,
} from '../state/piiMatrix.js';
import {
  COMPLIANCE_REGIMES,
  entitiesInScope,
  formatDossier,
} from '../state/compliance.js';
import { useAiSuggest } from '../state/useAiSuggest.js';
import { complianceNarrativePrompt } from '../state/aiPrompts.js';
import type { AiSuggestOptions } from '../state/aiPrompts.js';
import type { AskAtlasResult } from '../state/atlasApiTypes.js';
import { streamAskAtlas } from '../firebase/atlasApi.js';
import { useFocusScroll } from '../state/useFocusScroll.js';
import { Icon } from '../components/Icon.js';
import { ErrorState } from '../components/ErrorState.js';
import { AiMarkdown } from '../components/AiMarkdown.js';
import { AiThinking } from '../components/AiThinking.js';
import { DrillDownPanel } from '../components/DrillDownPanel.js';
import { EntityChip } from '../components/EntityChip.js';

export interface ComplianceConeViewProps {
  manifest: AtlasManifest;
  onNavigateEntity: (entityId: string) => void;
  /** Optional entity id to scroll to + highlight. */
  focus?: string;
  /** Injected in tests; defaults to streamAskAtlas. */
  onAsk?: (opts: AiSuggestOptions) => Promise<AskAtlasResult>;
}

/** Build a sample payload from an entity's fields using a default per type. */
function buildSamplePayload(
  manifest: AtlasManifest,
  entityId: string,
): Record<string, unknown> {
  const entity = manifest.entities.find((e) => e.id === entityId);
  if (!entity) return {};
  const out: Record<string, unknown> = {};
  for (const f of entity.fields) {
    out[f.name] = exampleFor(f.name, f.type);
  }
  return out;
}

function exampleFor(name: string, type: string): unknown {
  if (/email/i.test(name)) return 'jane@example.com';
  if (/phone/i.test(name)) return '+44 7700 900812';
  if (/dateOfBirth|dob/i.test(name)) return '1988-04-02';
  if (/ssn/i.test(name)) return 'XXX-XX-1234';
  if (/name/i.test(name)) return 'Jane D.';
  if (/^id$|Id$/.test(name)) return name === 'id' ? 'u-913' : 'r-' + Math.random().toString(36).slice(2, 6);
  if (type === 'number') return 42;
  if (type === 'boolean') return true;
  if (/At$|createdAt/.test(name)) return '2026-05-17';
  return 'sample';
}

export function ComplianceConeView({
  manifest,
  onNavigateEntity,
  focus,
  onAsk,
}: ComplianceConeViewProps): React.JSX.Element {
  useFocusScroll(focus);
  const [regime, setRegime] = useState<RegimeId>('gdpr');
  const [simEntityId, setSimEntityId] = useState<string | null>(null);

  const defaultOnAsk = useCallback(
    (opts: AiSuggestOptions) => streamAskAtlas(opts.prompt, opts.summary, opts.knownEntityIds),
    [],
  );
  const ai = useAiSuggest(onAsk ?? defaultOnAsk);

  // Reset AI state when the active regime changes.
  useEffect(() => {
    ai.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps — ai.clear is stable
  }, [regime]);

  const matrix = useMemo(() => buildPiiMatrix(manifest), [manifest]);
  const summary = matrix.summary[regime];

  // Group cells by entity for the matrix render.
  const byEntity = useMemo(() => {
    const map = new Map<string, typeof matrix.cells>();
    for (const cell of matrix.cells) {
      if (cell.regime !== regime) continue;
      if (!cell.applies) continue;
      const list = map.get(cell.entityId) ?? [];
      list.push(cell);
      map.set(cell.entityId, list);
    }
    return map;
  }, [matrix.cells, regime]);

  // Legacy dossier export (per the original cone view's COMPLIANCE_REGIMES).
  const downloadDossier = (): void => {
    // Map PR Q3 regime ids onto legacy COMPLIANCE_REGIMES where possible
    // (gdpr matches gdpr; others fall back to the first legacy regime).
    const legacyId = COMPLIANCE_REGIMES.some((r) => r.id === regime)
      ? regime
      : COMPLIANCE_REGIMES[0]!.id;
    const scoped = entitiesInScope(manifest, legacyId);
    const md = formatDossier(manifest, legacyId, scoped);
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-${regime}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const simSample = simEntityId ? buildSamplePayload(manifest, simEntityId) : null;
  const sim = simSample && simEntityId
    ? simulateScrubbed(simSample, simEntityId, manifest, regime)
    : null;

  return (
    <div className="compliance-cone">
      <header className="compliance-head">
        <h2>Compliance Cone-of-Light</h2>
        <div className="compliance-head-actions">
          <button className="btn btn-ghost" onClick={downloadDossier}>
            <Icon name="file_download" size="sm" />
            Export dossier · MD
          </button>
        </div>
      </header>

      <div className="cone-regime-strip" role="group" aria-label="Regime">
        <span className="cone-regime-label">REGIME</span>
        {REGIME_RULES.map((r) => {
          const s = matrix.summary[r.id];
          return (
            <button
              key={r.id}
              type="button"
              className={`cone-regime-chip${regime === r.id ? ' is-active' : ''}`}
              aria-pressed={regime === r.id}
              onClick={() => setRegime(r.id)}
            >
              {r.label} · {s.fieldCount} {s.fieldCount === 1 ? 'field' : 'fields'}
            </button>
          );
        })}
      </div>

      <p className="subtitle">
        Per-entity PII matrix for the selected regime. Click an entity
        to simulate a scrubbed API response.
      </p>

      <section className="cone-matrix" aria-label="PII matrix">
        {byEntity.size === 0 ? (
          <div className="lint-banner lint-banner-ok">
            <Icon name="check_circle" size="md" />
            <span>
              No fields are in {regime.toUpperCase()} scope — model is clean
              against this regime.
            </span>
          </div>
        ) : (
          <table className="cone-matrix-table">
            <thead>
              <tr>
                <th scope="col">Entity</th>
                <th scope="col">Field</th>
                <th scope="col">Regime</th>
                <th scope="col">Strip on export?</th>
                <th scope="col">Simulate</th>
              </tr>
            </thead>
            <tbody>
              {[...byEntity.entries()].map(([entityId, cells]) => (
                <ConeEntityBlock
                  key={entityId}
                  entityId={entityId}
                  cells={cells}
                  manifest={manifest}
                  regime={regime}
                  onSimulate={() => setSimEntityId(entityId)}
                  onOpenEntity={() => onNavigateEntity(entityId)}
                />
              ))}
            </tbody>
          </table>
        )}
        <p className="cone-summary" aria-live="polite">
          <strong>{summary.entityCount}</strong> {summary.entityCount === 1 ? 'entity' : 'entities'} ·{' '}
          <strong>{summary.fieldCount}</strong> {summary.fieldCount === 1 ? 'field' : 'fields'} in{' '}
          {regime.toUpperCase()} scope.
        </p>
      </section>

      {onAsk !== undefined && (
        <div className="ai-narrative-section">
          {!ai.answer && !ai.busy && !ai.error && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => {
                ai.clear();
                void ai.suggest(complianceNarrativePrompt(regime, manifest));
              }}
            >
              ✦ Generate narrative
            </button>
          )}

          {ai.busy && <AiThinking label="Reading the compliance cone…" />}

          {ai.answer && (
            <div className="ai-narrative-result">
              <AiMarkdown text={ai.answer} />
              <button className="btn btn-ghost btn-xs" onClick={ai.clear}>
                Dismiss
              </button>
            </div>
          )}

          {ai.error && (
            <ErrorState
              variant="inline"
              message={ai.error}
              onDismiss={ai.clear}
              onRetry={() => {
                ai.clear();
                void ai.suggest(complianceNarrativePrompt(regime, manifest));
              }}
            />
          )}
        </div>
      )}

      <DrillDownPanel
        open={sim !== null}
        title={simEntityId ? `Simulate response · ${simEntityId}` : ''}
        subtitle={`Regime: ${regime.toUpperCase()} · ${sim?.redactedKeys.length ?? 0} redacted`}
        onClose={() => setSimEntityId(null)}
      >
        {sim && simSample && simEntityId && (
          <div className="cone-sim">
            <div className="cone-sim-section">
              <span className="cone-sim-label">RAW · GET /{simEntityId.toLowerCase()}s/{(simSample.id as string) ?? 'sample'}</span>
              <pre className="cone-sim-payload">
                {jsonPretty(simSample)}
              </pre>
            </div>
            <div className="cone-sim-section">
              <span className="cone-sim-label">SCRUBBED · regime: {regime.toUpperCase()}</span>
              <pre className="cone-sim-payload cone-sim-payload-scrubbed">
                {jsonPretty(sim.scrubbed)}
              </pre>
            </div>
            <div className="cone-sim-actions">
              <EntityChip entityId={simEntityId} manifest={manifest} onClick={onNavigateEntity} />
              {sim.redactedKeys.length === 0 && (
                <span className="cone-sim-clean">✓ no PII redacted under this regime</span>
              )}
            </div>
          </div>
        )}
      </DrillDownPanel>
    </div>
  );
}

function jsonPretty(obj: Record<string, unknown>): string {
  return JSON.stringify(obj, null, 2);
}

function ConeEntityBlock({
  entityId, cells, manifest, regime, onSimulate, onOpenEntity,
}: {
  entityId: string;
  cells: Array<{ field: string; applies: boolean; stripOnExport: boolean }>;
  manifest: AtlasManifest;
  regime: RegimeId;
  onSimulate: () => void;
  onOpenEntity: () => void;
}): React.JSX.Element {
  return (
    <>
      <tr className="cone-matrix-row-entity">
        <td colSpan={5}>
          <div className="cone-matrix-entity-row">
            <EntityChip entityId={entityId} manifest={manifest} onClick={onOpenEntity} />
            <span className="cone-matrix-fieldcount mono">
              {cells.length} {cells.length === 1 ? 'field' : 'fields'}
            </span>
            <button type="button" className="btn btn-ghost cone-sim-btn" onClick={onSimulate}>
              <Icon name="play_arrow" size="sm" />
              Simulate response
            </button>
          </div>
        </td>
      </tr>
      {cells.map((c) => (
        <tr key={c.field} className="cone-matrix-row-field">
          <td />
          <td className="mono">{c.field}</td>
          <td><span className={`chip ${toneFor(regime)}`}>{regime.toUpperCase()}</span></td>
          <td>{c.stripOnExport ? <span className="chip good">yes</span> : <span className="chip">no</span>}</td>
          <td />
        </tr>
      ))}
    </>
  );
}

function toneFor(regime: RegimeId): string {
  if (regime === 'gdpr') return 'brand';
  if (regime === 'ccpa') return 'info';
  return 'danger';
}
